import React, { useMemo } from 'react';
import { Outlet, useLocation, Navigate, useNavigate, useParams } from 'react-router-dom';
import { CollaboratorNavigation, ProfileHeader } from '@/features/Collaborators/Shared';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { useCollaboratorProfile } from '@/features/Collaborators/Data/hooks/useCollaboratorProfile';
import { useAuth, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_ADMIN, ROLE_ADMIN, ROLE_COLLABORATOR_EDITOR } from '@/contexts/AuthContext';

export default function CollaboratorsLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const { userHasRole } = useAuth();

    // Check if user has access to the full list
    const hasListAccess = userHasRole(ROLE_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_VIEWER) ||
        userHasRole(ROLE_COLLABORATOR_EDITOR);

    const canEditCollaborator = userHasRole(ROLE_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_ADMIN) ||
        userHasRole(ROLE_COLLABORATOR_EDITOR);

    // Fetch list only if has access
    const { data: collaboratorsList = [], isLoading: isListLoading, error: listError } = useCollaboratorsRealtimeQuery({
        enabled: hasListAccess
    });

    // Fetch profile if id is present (handles public/self/private logic internally)
    const { collaborator: profileCollaborator, isLoading: isProfileLoading } = useCollaboratorProfile(id);

    const collaborators = hasListAccess ? collaboratorsList : [];
    const isLoading = hasListAccess ? isListLoading : isProfileLoading;
    const error = hasListAccess ? listError : null;

    const isProfilePage = useMemo(() => Boolean(id), [id]);

    const selectedCollaborator = useMemo(
        () => {
            if (profileCollaborator) return profileCollaborator as any;
            return collaborators.find((collaborator) => collaborator.id === id);
        },
        [collaborators, id, profileCollaborator]
    );

    if (location.pathname === '/collaborators' || location.pathname === '/collaborators/') {
        return <Navigate to="directory" replace />;
    }

    return (
        <div>
            {isProfilePage ? (
                <ProfileHeader
                    collaborator={selectedCollaborator}
                    onBack={() => navigate(-1)}
                    // Trocar foto: só na edição do perfil — mesma regra de quem acessa essa
                    // página (admin, collaborator_admin, collaborator_editor).
                    photoEditable={location.pathname.endsWith('/edit') && canEditCollaborator}
                />
            ) : (
                <CollaboratorNavigation collaborators={collaborators} isLoading={isLoading} />
            )}
            <main>
                <Outlet context={{ collaborators, isLoading, error }} />
            </main>
        </div>
    );
}
